import React, { useMemo, type RefObject } from "react";
import { type TextInput } from "react-native";
import { View, StyleSheet } from "react-native";
import ChatComposerBar, {
  type ComposerAttachment,
} from "../../components/chatNewInterface/chatLayout/messageComposer/ChatComposerBar";
import {
  type ToolId,
  type ToolOption,
} from "../../components/chatNewInterface/interfaceComponents/ToolsMenu/toolOptions";
import { type ChatSpeechInputPhase } from "../../hooks/useChatSpeechInput";
import { type PlaybackSpeed } from "../../utils/toolPreferenceStorage";

interface AttachedItem {
  id: string;
  name: string;
  duedate?: string | number | Date;
}

interface AttachedFile {
  id: string;
  fileName: string;
  fileURL?: string;
  isLoading?: boolean;
}

const todoSuggestions = [
  { text: "Give me a game plan for this" },
  { text: "Break this into subtasks" },
  { text: "Build me a timeline to finish this" },
  { text: "What resources or materials do I need?" },
];

const fileSuggestions = [
  { text: "Summarize this for me" },
  { text: "Quiz me on this material" },
  { text: "What would likely be on a test about this?" },
  { text: "What are the key takeaways?" },
];

const assignmentSuggestions = [
  { text: "What should I do first?" },
  { text: "Break this into smaller steps" },
  { text: "What topics should I review first?" },
  { text: "How will this be graded?" },
];
const DEFAULT_PLAYBACK_SPEED: PlaybackSpeed = 1;

interface ChatInputProps {
  value: string;
  voiceInputErrorMessage?: string | null;
  onChangeText: (text: string) => void;
  onSubmit: (message: string) => void;
  inputRef?: RefObject<TextInput>;
  disabled?: boolean;
  placeholder?: string;
  showSuggestions?: boolean;
  activeWorkspaceType: "general" | "course" | "support";
  tools: ToolOption[];
  activeToolId: ToolId | null;
  onToolSelect: (toolId: ToolId) => void;
  mathModeEnabled: boolean;
  onMathModeToggle: (enabled: boolean) => void;
  isAudioOn: boolean;
  onAudioToggleChange: (enabled: boolean) => void;
  playbackSpeed: PlaybackSpeed;
  onPlaybackSpeedChange: (speed: PlaybackSpeed) => void;
  attachedTasks: AttachedItem[];
  attachedAssignments: AttachedItem[];
  attachedFiles: AttachedFile[];
  onAddTask: () => void;
  onAddAssignment: () => void;
  onAddFile: () => void;
  onRemoveAttachment: () => void;
  voiceInputPhase: ChatSpeechInputPhase;
  audioLevel?: number;
  onRecordingStartPress: () => void;
  onRecordingCancelPress: () => void;
  onRecordingStopPress: () => void;
  taskModal: React.ReactNode;
  assignmentModal: React.ReactNode;
}

const ChatInput = ({
  value,
  voiceInputErrorMessage,
  onChangeText,
  onSubmit,
  inputRef,
  disabled,
  placeholder,
  showSuggestions = true,
  attachedTasks,
  attachedAssignments,
  attachedFiles,
  tools,
  activeToolId,
  onToolSelect,
  mathModeEnabled,
  onMathModeToggle,
  isAudioOn,
  onAudioToggleChange,
  playbackSpeed,
  onPlaybackSpeedChange,
  onAddTask,
  onAddAssignment,
  onAddFile,
  onRemoveAttachment,
  voiceInputPhase,
  audioLevel = 0,
  onRecordingStartPress,
  onRecordingCancelPress,
  onRecordingStopPress,
  taskModal,
  assignmentModal,
}: ChatInputProps) => {
  const attachment = useMemo<ComposerAttachment | null>(() => {
    if (attachedFiles.length > 0) {
      const file = attachedFiles[0];
      return { id: file.id, name: file.fileName, type: "file", isLoading: file.isLoading };
    }

    if (attachedAssignments.length > 0) {
      const assignment = attachedAssignments[0];
      return {
        id: assignment.id,
        name: assignment.name,
        type: "assignment",
        duedate: assignment.duedate,
      };
    }

    if (attachedTasks.length > 0) {
      const task = attachedTasks[0];
      return { id: task.id, name: task.name, type: "task", duedate: task.duedate };
    }

    return null;
  }, [attachedAssignments, attachedFiles, attachedTasks]);

  const isAttachmentLoading = useMemo(() => attachedFiles.some((file) => file.isLoading), [attachedFiles]);

  const suggestedPrompts = useMemo(() => {
    if (!showSuggestions) {
      return [];
    }
    if (attachedTasks.length > 0) {
      return todoSuggestions;
    }
    if (attachedFiles.length > 0) {
      return fileSuggestions;
    }
    if (attachedAssignments.length > 0) {
      return assignmentSuggestions;
    }
    return [];
  }, [attachedAssignments, attachedFiles, attachedTasks, showSuggestions]);

  return (
    <View style={styles.container}>
      <ChatComposerBar
        value={value}
        voiceInputPhase={voiceInputPhase}
        voiceInputErrorMessage={voiceInputErrorMessage}
        audioLevel={audioLevel}
        isAudioOn={isAudioOn}
        placeholder={placeholder ?? "Ask Axio..."}
        inputRef={inputRef}
        onChangeText={onChangeText}
        onSubmitEditing={onSubmit}
        isDisabled={disabled ?? false}
        onRecordingStartPress={onRecordingStartPress}
        onRecordingCancelPress={onRecordingCancelPress}
        onRecordingStopPress={onRecordingStopPress}
        onAudioOnPress={() => onAudioToggleChange(true)}
        onAudioOffPress={() => onAudioToggleChange(false)}
        chatContext="companion"
        tools={tools}
        activeToolId={activeToolId}
        onToolSelect={onToolSelect}
        mathModeEnabled={mathModeEnabled}
        onMathModeToggle={onMathModeToggle}
        playbackSpeed={playbackSpeed ?? DEFAULT_PLAYBACK_SPEED}
        onPlaybackSpeedChange={onPlaybackSpeedChange}
        isAttachmentEnabled={true}
        attachment={attachment}
        isAttachmentLoading={isAttachmentLoading}
        onAttachmentPress={onAddFile}
        onAddTaskPress={onAddTask}
        onAddAssignmentPress={onAddAssignment}
        onDeleteAttachmentPress={onRemoveAttachment}
        suggestedPrompts={suggestedPrompts}
        onSuggestionPress={onChangeText}
      />

      {taskModal}
      {assignmentModal}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
});

export default ChatInput;
